/**
 * usePushNotifications — Web Push subscription
 * Il Diario di Bordo - Frontend
 *
 * Gestione consenso + iscrizione Web Push lato client:
 *  - requestPermission() → richiede permesso browser
 *  - subscribe() → crea PushSubscription e la registra sul backend
 *  - unsubscribe() → rimuove la subscription corrente
 *  - permission, isSubscribed, isSupported, error → stati
 *
 * Su iOS richiede PWA installata + iOS 16.4+. Su Safari macOS 16.4+.
 */

import { useEffect, useState, useCallback } from 'react'
import api from '../services/api'

/**
 * Helper standalone (usabile fuori dai componenti React):
 * - Disiscrive la PushSubscription corrente dal browser
 * - Cancella il record server-side
 * Chiamare da AuthContext.logout() PRIMA del logout backend.
 *
 * @param {Object} options
 * @param {Boolean} options.swallowErrors - se true (default), non rilancia eccezioni
 *                                          (logout non deve fallire per problemi push)
 */
export async function cleanupPushSubscription({ swallowErrors = true } = {}) {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return false

  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    if (!sub) return true

    const endpoint = sub.endpoint
    // Prima cancella server-side (con cookie ancora valido!), poi unsubscribe locale
    try {
      await api.delete('/push-subscriptions', { data: { endpoint } })
    } catch (e) {
      if (!swallowErrors) throw e
    }
    try {
      await sub.unsubscribe()
    } catch (e) {
      if (!swallowErrors) throw e
    }
    return true
  } catch (err) {
    if (!swallowErrors) throw err
    console.warn('[cleanupPushSubscription] non-fatal:', err.message)
    return false
  }
}

/**
 * Helper standalone: sincronizza la subscription locale con il backend.
 * Da chiamare DOPO il login: se nel browser esiste già una subscription
 * (es. perché un altro utente si era iscritto su questo device), il backend
 * la riassocia all'utente corrente (idempotente via upsert by endpoint).
 *
 * Restituisce true se ha sincronizzato, false se non c'era nulla da fare.
 */
export async function syncPushSubscriptionWithCurrentUser({ swallowErrors = true } = {}) {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return false

  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    if (!sub) return false

    const subJson = sub.toJSON()
    await api.post('/push-subscriptions', {
      endpoint: subJson.endpoint,
      keys: subJson.keys,
      platform: detectPlatform(),
      browser: detectBrowser()
    })
    return true
  } catch (err) {
    if (!swallowErrors) throw err
    console.warn('[syncPushSubscriptionWithCurrentUser] non-fatal:', err.message)
    return false
  }
}

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const arr = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) arr[i] = rawData.charCodeAt(i)
  return arr
}

const detectPlatform = () => {
  const ua = navigator.userAgent || ''
  const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true

  if (/iPhone|iPad|iPod/i.test(ua)) return isStandalone ? 'ios-pwa' : 'desktop-web'
  if (/Android/i.test(ua)) return isStandalone ? 'android-pwa' : 'android-web'
  if (/Macintosh/i.test(ua)) return isStandalone ? 'macos-pwa' : 'desktop-web'
  return 'desktop-web'
}

const detectBrowser = () => {
  const ua = navigator.userAgent || ''
  if (/Edg\//.test(ua)) return 'edge'
  if (/OPR\//.test(ua)) return 'opera'
  if (/Brave/.test(ua) || navigator.brave) return 'brave'
  if (/Chrome\//.test(ua)) return 'chrome'
  if (/Firefox\//.test(ua)) return 'firefox'
  if (/Safari\//.test(ua)) return 'safari'
  return 'other'
}

export function usePushNotifications() {
  const isSupported = typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window

  const [permission, setPermission] = useState(isSupported ? Notification.permission : 'unsupported')
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const refreshSubscriptionStatus = useCallback(async () => {
    if (!isSupported) return false
    try {
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()
      setIsSubscribed(Boolean(sub))
      return Boolean(sub)
    } catch (err) {
      setError(err.message)
      return false
    }
  }, [isSupported])

  useEffect(() => {
    if (!isSupported) return
    refreshSubscriptionStatus()
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'notifications' })
        .then((status) => {
          setPermission(status.state === 'prompt' ? 'default' : status.state)
          status.onchange = () => {
            setPermission(status.state === 'prompt' ? 'default' : status.state)
          }
        })
        .catch(() => { /* ignora */ })
    }
  }, [isSupported, refreshSubscriptionStatus])

  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      setError('Browser non supportato')
      return 'unsupported'
    }
    const result = await Notification.requestPermission()
    setPermission(result)
    return result
  }, [isSupported])

  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Browser non supportato')
      return null
    }
    setBusy(true)
    setError(null)
    try {
      // 1. Richiedi permission se non già concesso
      if (Notification.permission === 'default') {
        const result = await Notification.requestPermission()
        setPermission(result)
        if (result !== 'granted') {
          setBusy(false)
          return null
        }
      } else if (Notification.permission === 'denied') {
        setError('Permesso notifiche negato dal browser')
        setBusy(false)
        return null
      }

      // 2. Recupera VAPID public key dal backend
      const { data: keyResponse } = await api.get('/push-subscriptions/public-key')
      const publicKey = keyResponse?.publicKey
      if (!publicKey) throw new Error('VAPID public key non disponibile')

      // 3. Crea PushSubscription
      const reg = await navigator.serviceWorker.ready
      let sub = await reg.pushManager.getSubscription()
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey)
        })
      }

      // 4. Registra subscription sul backend
      const subJson = sub.toJSON()
      await api.post('/push-subscriptions', {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        platform: detectPlatform(),
        browser: detectBrowser()
      })

      setIsSubscribed(true)
      return sub
    } catch (err) {
      console.error('[usePushNotifications] subscribe error:', err)
      setError(err.message || 'Errore iscrizione')
      return null
    } finally {
      setBusy(false)
    }
  }, [isSupported])

  const unsubscribe = useCallback(async () => {
    if (!isSupported) return false
    setBusy(true)
    setError(null)
    try {
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()
      if (!sub) {
        setIsSubscribed(false)
        return true
      }
      const endpoint = sub.endpoint
      await sub.unsubscribe()
      try {
        await api.delete('/push-subscriptions', { data: { endpoint } })
      } catch (e) {
        // se la chiamata fallisce, lato browser è già scollegato — log soltanto
        console.warn('[usePushNotifications] delete subscription backend failed:', e.message)
      }
      setIsSubscribed(false)
      return true
    } catch (err) {
      setError(err.message || 'Errore disiscrizione')
      return false
    } finally {
      setBusy(false)
    }
  }, [isSupported])

  const sendTest = useCallback(async () => {
    try {
      const { data } = await api.post('/push-subscriptions/test')
      return data?.data || data
    } catch (err) {
      setError(err.message || 'Errore test')
      return null
    }
  }, [])

  return {
    isSupported,
    permission,
    isSubscribed,
    busy,
    error,
    requestPermission,
    subscribe,
    unsubscribe,
    sendTest,
    refreshSubscriptionStatus
  }
}
